import { ReactNode } from 'react';
import { ColumnsType } from 'antd/lib/table';
import {
  DatesAtAGlanceRow,
  OptionProps,
  OverdueTaskRow,
  ProjectDurationRow,
  ReportsFilters,
  ReportsSortBy,
  StructuralPassFailRow,
} from '@models/interfaces';
import { EMPTY_VALUE } from '../constants';
import { getTableSortOrder } from '../helpers';

export const isSortByAllowed = (
  sortByOptions: OptionProps[],
  sortBy: ReportsSortBy,
): boolean => sortByOptions.some(({ value }: OptionProps) => value === sortBy);

export const renderValue = (value?: unknown): ReactNode =>
  value === undefined || value === null || value === ''
    ? EMPTY_VALUE
    : String(value);

export const renderDisplayValue = (
  display?: string | null,
  values?: string[],
): ReactNode => renderValue(display || values?.join(', '));

export const getSeverityClassName = (daysOverdue?: number): string => {
  if (!daysOverdue || daysOverdue <= 7) {
    return 'prov-overdue-tasks-report__row--low';
  }

  if (daysOverdue <= 14) {
    return 'prov-overdue-tasks-report__row--medium';
  }

  if (daysOverdue <= 30) {
    return 'prov-overdue-tasks-report__row--high';
  }

  return 'prov-overdue-tasks-report__row--critical';
};

export const getGenericSortableColumn = (
  dataIndex: string,
  title: string,
  sortBy: ReportsSortBy,
  filters: ReportsFilters,
): ColumnsType<any>[number] => ({
  dataIndex,
  key: sortBy,
  sorter: true,
  sortOrder:
    filters.sortBy === sortBy
      ? getTableSortOrder(filters.sortOrder)
      : undefined,
  title,
});

export const getOverdueSortableColumn = (
  dataIndex: keyof OverdueTaskRow,
  title: string,
  sortBy: ReportsSortBy,
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
  render?: (value: unknown, record: OverdueTaskRow) => ReactNode,
): ColumnsType<OverdueTaskRow>[number] => {
  const isSortable = isSortByAllowed(sortByOptions, sortBy);

  return {
    dataIndex,
    key: sortBy,
    render,
    sorter: isSortable,
    sortOrder:
      isSortable && filters.sortBy === sortBy
        ? getTableSortOrder(filters.sortOrder)
        : undefined,
    title,
  };
};

export const getDatesAtAGlanceSortableColumn = (
  dataIndex: keyof DatesAtAGlanceRow,
  title: string,
  sortBy: ReportsSortBy,
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
  render?: (value: unknown, record: DatesAtAGlanceRow) => ReactNode,
): ColumnsType<DatesAtAGlanceRow>[number] => {
  const isSortable = isSortByAllowed(sortByOptions, sortBy);

  return {
    dataIndex,
    key: sortBy,
    render,
    sorter: isSortable,
    sortOrder:
      isSortable && filters.sortBy === sortBy
        ? getTableSortOrder(filters.sortOrder)
        : undefined,
    title,
  };
};

export const getStructuralSortableColumn = (
  dataIndex: keyof StructuralPassFailRow,
  title: string,
  sortBy: ReportsSortBy,
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
  render?: (value: unknown, record: StructuralPassFailRow) => ReactNode,
): ColumnsType<StructuralPassFailRow>[number] => {
  const isSortable = isSortByAllowed(sortByOptions, sortBy);

  return {
    dataIndex,
    key: sortBy,
    render,
    sorter: isSortable,
    sortOrder:
      isSortable && filters.sortBy === sortBy
        ? getTableSortOrder(filters.sortOrder)
        : undefined,
    title,
  };
};

export const getProjectDurationSortableColumn = (
  dataIndex: keyof ProjectDurationRow,
  title: string,
  sortBy: ReportsSortBy,
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
  render?: (value: unknown, record: ProjectDurationRow) => ReactNode,
): ColumnsType<ProjectDurationRow>[number] => {
  const isSortable = isSortByAllowed(sortByOptions, sortBy);

  return {
    dataIndex,
    key: sortBy,
    render,
    sorter: isSortable,
    sortOrder:
      isSortable && filters.sortBy === sortBy
        ? getTableSortOrder(filters.sortOrder)
        : undefined,
    title,
  };
};

export const renderBooleanValue = (value?: boolean | null): ReactNode => {
  if (value === true) {
    return 'Yes';
  }

  if (value === false) {
    return 'No';
  }

  return EMPTY_VALUE;
};

export const renderPercentValue = (value?: unknown): ReactNode =>
  typeof value === 'number' ? `${value}%` : EMPTY_VALUE;
